import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import { ArrowUpRight, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import TextAreaChat from '@/components/TextAreaChat';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useState, useMemo, useEffect } from 'react';
import { Content, Conversation, Model } from '@shared/types';
import { MessageItem } from '../types/misc.ts';
import { LimitReachedMessage } from '@/components/LimitReachedMessage';
import { LowPromptsWarningMessage } from '@/components/LowPromptsWarningMessage';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/lib/utils';
import { SelectedItemsContext } from '@/contexts/SelectedItemsContext';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';
import { useSendContentMutation } from '@/services/messageService';
import { useProfile } from '@/services/profileService';
import { useAppConfig } from '@/contexts/AppConfigContext';

export function PromptView() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, billing, isLoading } = useAuth();
  const totalTokens = billing?.tokens.total ?? 0;
  const { data: profile, isLoading: isProfileLoading } = useProfile();
  const { isSidebarOpen } = useOutletContext<{ isSidebarOpen: boolean }>();
  const queryClient = useQueryClient();
  const { config } = useAppConfig();

  const firstName = useMemo(() => {
    // Wait until the profile query resolves for signed-in users so the
    // greeting doesn't flash the email local-part before snapping to the
    // real first name.
    if (user && isProfileLoading) return '';
    const source = profile?.full_name || user?.email?.split('@')[0] || '';
    return source.trim().split(/\s+/)[0] || '';
  }, [profile?.full_name, user, isProfileLoading]);

  const [type, setType] = useState<'parametric' | 'creative'>('parametric');

  const [model, setModel] = useState<Model>('google/gemini-3.1-pro-preview');

  const handleTypeChange = (newType: 'parametric' | 'creative') => {
    setType(newType);
    // Reset model to the default for the new type
    if (newType === 'creative') {
      setModel('quality');
    } else {
      setModel('google/gemini-3.1-pro-preview');
    }
  };

  const [isLoaded, setIsLoaded] = useState(false);
  const isMobile = useIsMobile();
  const [images, setImages] = useState<MessageItem[]>([]);
  const [mesh, setMesh] = useState<MessageItem | null>(null);

  const newConversationId = useMemo(() => {
    return crypto.randomUUID();
  }, []);

  const lowPrompts = useMemo(() => {
    if (isLoading) return false;
    return totalTokens > 0 && totalTokens <= 10;
  }, [totalTokens, isLoading]);

  const limitReached = useMemo(() => {
    if (isLoading) return false;
    return totalTokens <= 0;
  }, [totalTokens, isLoading]);

  const { mutate: sendMessage } = useSendContentMutation({
    conversation: {
      id: newConversationId,
      user_id: user?.id ?? '',
      type: type,
      settings: { model: model },
      current_message_leaf_id: null,
    },
  });

  // Trigger fade in on mount
  useEffect(() => {
    // Use requestAnimationFrame to ensure the initial render is complete
    const frame = requestAnimationFrame(() => {
      setIsLoaded(true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  // Helper function to get time-based greeting (memoized for performance)
  const getTimeBasedGreeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) {
      return 'Good morning';
    } else if (hour < 18) {
      return 'Good afternoon';
    } else {
      return 'Good evening';
    }
  }, []); // Empty dependency array means it only calculates once per page load

  const { mutate: handleGenerate } = useMutation({
    mutationFn: async (content: Content) => {
      posthog.capture('new_conversation', {
        type: type,
        model_name: model,
        text: (content.text ?? '').trim().slice(0, 100),
        image_count: content.images?.length ?? 0,
        mesh_count: content.mesh ? 1 : 0,
        conversation_id: newConversationId,
      });

      // Create conversation immediately with 'New Conversation'
      const { data: conversation, error: conversationError } = await supabase
        .from('conversations')
        .insert([
          {
            id: newConversationId,
            user_id: user?.id ?? '',
            // Title will be generated by the title-generator function
            title: 'New Conversation',
            type: type,
            settings: {
              model: model,
            },
          },
        ])
        .select()
        .single();

      if (conversationError) throw conversationError;

      sendMessage(content);

      return {
        conversationId: conversation.id,
        content: content,
      };
    },
    onSuccess: (data) => {
      // Generate title in the background if there's content
      supabase.functions
        .invoke('title-generator', {
          body: { content: data.content, conversationId: data.conversationId },
        })
        .then(({ data: titleData, error }) => {
          if (!error && titleData?.title) {
            // Update conversation title once generated
            supabase
              .from('conversations')
              .update({ title: titleData.title })
              .eq('id', data.conversationId)
              .then(() => {
                queryClient.invalidateQueries({
                  queryKey: ['conversations'],
                });

                queryClient.setQueryData(
                  ['conversation', data.conversationId],
                  (oldConversation: Conversation) => ({
                    ...oldConversation,
                    title: titleData.title,
                  }),
                );
              });
          }
        })
        .catch((error) => {
          console.error('Error generating title:', error);
        });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      navigate(`/editor/${data.conversationId}`);
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast({
        title: 'Error',
        description:
          error instanceof Error ? error.message : 'Failed to process prompt',
        variant: 'destructive',
      });
    },
  });

  return (
    <div
      className={cn(
        'relative h-full min-h-full w-full transition-all duration-300 ease-in-out',
        isSidebarOpen && !isMobile && user?.id && 'pb-6 pr-6 pt-6',
      )}
    >
      <div
        className={cn(
          'h-full min-h-full bg-adam-bg-secondary-dark',
          isSidebarOpen &&
            !isMobile &&
            user?.id &&
            'rounded-xl shadow-[0_0_15px_rgba(0,0,0,0.1)]',
        )}
      >
        {!user && (
          <div className="fixed right-4 top-4 z-10 flex flex-row gap-2">
            <Button
              variant="light"
              onClick={() => navigate('/signup')}
              className="w-auto"
            >
              Sign Up
            </Button>
            <Button onClick={() => navigate('/signin')} className="w-auto">
              <LogIn className="mr-2 h-4 w-4" />
              Sign In
            </Button>
          </div>
        )}

        <main className="flex h-full w-full flex-col items-center justify-center px-4 md:px-8">
          <div className="mx-auto flex max-w-3xl flex-col items-center justify-center">
            <h1
              className={cn(
                'mb-8 text-center text-2xl font-medium text-adam-text-primary md:text-3xl lg:text-4xl',
                'motion-safe:transition-opacity motion-safe:duration-1000 motion-safe:ease-out',
                isLoaded ? 'opacity-100' : 'opacity-0',
              )}
            >
              {getTimeBasedGreeting}
              {firstName ? `, ${firstName}` : ''}!
            </h1>
          </div>
          <div className="flex w-full flex-col items-center">
            <div className="w-full max-w-3xl space-y-4 pb-12">
              <SelectedItemsContext.Provider
                value={{ images, setImages, mesh, setMesh }}
              >
                <TextAreaChat
                  onSubmit={handleGenerate}
                  conversation={{
                    id: newConversationId,
                    user_id: user?.id ?? '',
                  }}
                  onFocus={() => {
                    if (!user) {
                      navigate('/signin');
                      return;
                    }
                  }}
                  placeholder={config?.tagline || "Start building with Gienox..."}
                  type={type}
                  disabled={limitReached}
                  model={model}
                  setModel={setModel}
                  showPromptGenerator={true}
                  showFullLabels={true}
                  onTypeChange={handleTypeChange}
                />
              </SelectedItemsContext.Provider>

              {/* Extension link */}
              <div className="mt-3 flex justify-center">
                <Link
                  to="/fusion-install"
                  className="group inline-flex items-center gap-1.5 rounded-full border border-adam-neutral-800 bg-adam-neutral-900/50 px-4 py-2 text-sm text-adam-neutral-300 transition-all hover:border-adam-blue/40 hover:bg-adam-blue/10 hover:text-adam-blue"
                >
                  Try our{' '}
                  <span className="font-medium text-adam-blue">
                    Fusion 360 extension
                  </span>
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </Link>
              </div>
              <div className="relative">
                {isLoading && (
                  <div className="absolute left-0 right-0 top-0">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-adam-blue border-t-transparent" />
                  </div>
                )}
                {!isLoading && user && limitReached && (
                  <div className="absolute left-0 right-0 top-0">
                    <LimitReachedMessage />
                  </div>
                )}
                {!isLoading && user && lowPrompts && !limitReached && (
                  <div className="absolute left-0 right-0 top-0">
                    <LowPromptsWarningMessage tokensRemaining={totalTokens} />
                  </div>
                )}
              </div>

              {!user && (
                <p className="text-center text-sm text-gray-500">
                  <Link
                    to="/signin"
                    className="!text-adam-blue hover:!text-adam-blue/80"
                  >
                    Sign in
                  </Link>{' '}
                  or{' '}
                  <Link
                    to="/signup"
                    className="!text-adam-blue hover:!text-adam-blue/80"
                  >
                    create an account
                  </Link>{' '}
                  to start generating
                </p>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
